import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "next-mdx-import-source-file";
import {Layout, Paragraph, Strong} from "../../../components/blog/layout";
import {DotDotDot, Image, HeaderWithAnchor, Note, Callout, FootnoteLink, Footnote, YouTube, EmbedLink, EmbedGithubGist} from "../../../components/blog/helpers";
const MDXLayout = Layout({
  postId: "unreal-4-enums-and-accidental-pointer-arithmetic"
});
function _createMdxContent(props) {
  const _components = {
    a: "a",
    code: "code",
    em: "em",
    li: "li",
    p: "p",
    strong: "strong",
    ul: "ul",
    ..._provideComponents(),
    ...props.components
  };
  return _jsxs(_Fragment, {
    children: [_jsx(_components.p, {
      children: "While writing some code the other day, a colleague noticed that some code that was meant to only run under a specific but common condition was actually never getting called. It took us a while to track down the issue, not because it was particularly hard to solve — but because it was a single character typo that was incredibly hard to see!"
    }), "\n", _jsx(_components.p, {
      children: "No specific knowledge of UE4 or game engines is required to follow this article, though it may help; this is possible in any C++ code."
    }), "\n", _jsx(HeaderWithAnchor, {
      id: "at-a-glance",
      number: 1,
      children: _jsx(_components.p, {
        children: "At a glance 👀"
      })
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: "C enums can be implicitly converted to int"
      }), "\n", _jsx(_components.li, {
        children: "Pointers can be added and subtracted to and from"
      }), "\n", _jsx(_components.li, {
        children: "Use enum class instead of enum"
      }), "\n"]
    }), "\n", _jsx(Image, {
      src: require("./1_raccoon.jpeg"),
      srcSmall: require("./1_raccoon.jpeg"),
      alt: "A cute raccoon.",
      caption: _jsx(_Fragment, {
        children: "Some typos can be really hard to track down, so we should strive to write code that is resilient to them."
      }),
      width: "1000",
      height: "667",
      fullWidth: true
    }), "\n", _jsx(HeaderWithAnchor, {
      id: "some-definitions",
      number: 2,
      children: _jsx(_components.p, {
        children: "Some definitions"
      })
    }), "\n", _jsxs(_components.p, {
      children: ["In UE4, an Actor is a class that interacts with the “real world” in the game. For example, this might be a car, a horse, or a power-up. Actors can “own” other actors. In our case, a Player may own a Sword. They’re two distinct actors with their own behaviours, but in this case the sword clearly belongs to, or is ", _jsx(_components.em, {
        children: "owned by"
      }), ", the player."]
    }), "\n", _jsxs(_components.p, {
      children: ["Each actor has a net mode. You don’t need to know what a net-mode is for the purposes of this article, just know that it could be one of the following values: ", _jsx(_components.code, {
        children: "NM_Client"
      }), ", ", _jsx(_components.code, {
        children: "NM_Standalone"
      }), ", ", _jsx(_components.code, {
        children: "NM_DedicatedServer"
      }), "."]
    }), "\n", _jsx(HeaderWithAnchor, {
      id: "lets-look-at-some-code",
      number: 3,
      children: _jsx(_components.p, {
        children: "Let’s look at some code 🐊"
      })
    }), "\n", _jsxs(_components.p, {
      children: ["Consider the class ", _jsx(_components.code, {
        children: "AActor"
      }), ", which exposes at least the following two functions:"]
    }), "\n", _jsx(EmbedGithubGist, {
      id: "ab306f059a7491498d1673f2336b0023"
    }), "\n", _jsx(_components.p, {
      children: "There are cases where the owner of some actor has a different net mode to the actor itself. This might be if say, we’ve attached a purely client-side visual effect to a character, like a lit lamp."
    }), "\n", _jsx(_components.p, {
      children: "In this case, from the lamp’s code, we may want to know what the net mode of our owning actor is. This is a very simple piece of code to write:"
    }), "\n", _jsx(EmbedGithubGist, {
      id: "48f8f0f55ac6c9638e0ad87eaca05d03"
    }), "\n", _jsx(_components.p, {
      children: "However, it’s possible to make a hard-to-spot typo that completely changes the meaning of the code."
    }), "\n", _jsx(EmbedGithubGist, {
      id: "ec2ad7b220a1a75efaeb25d66b810860"
    }), "\n", _jsx(_components.p, {
      children: "See the typo?"
    }), "\n", _jsx(EmbedGithubGist, {
      id: "93ef2a190a965fd3076964195d21896f"
    }), "\n", _jsx(_components.p, {
      children: "Here, instead of asking our owner what its net mode is, we’re in fact taking the address of our owner, and subtracting our current net mode from it."
    }), "\n", _jsx(_components.p, {
      children: "“Surely!” I hear you say, “this won’t compile. You’re mixing types with subtraction!”"
    }), "\n", _jsx(_components.p, {
      children: "The effect of having this typo is that the comparison would always return false, meaning expected behaviour wasn’t being run."
    }), "\n", _jsx(HeaderWithAnchor, {
      id: "enter-enums",
      number: 4,
      children: _jsx(_components.p, {
        children: "Enter enums and pointer arithmetic ➕"
      })
    }), "\n", _jsxs(_components.p, {
      children: ["Let’s take a look at the definition of ", _jsx(_components.code, {
        children: "ENetMode"
      }), "."]
    }), "\n", _jsx(EmbedGithubGist, {
      id: "1bc304b14d393aa35e4d0ec2fb763bb5"
    }), "\n", _jsxs(_components.p, {
      children: ["It’s a regular C style ", _jsx(_components.code, {
        children: "enum"
      }), ". It is not a C++ ", _jsx(_components.code, {
        children: "enum class"
      }), ", and this is important. (These are known as ", _jsx(_components.strong, {
        children: "unscoped enums"
      }), " and ", _jsx(_components.strong, {
        children: "scoped enums"
      }), " respectively. You can also use ", _jsx(_components.code, {
        children: "enum struct"
      }), " instead of ", _jsx(_components.code, {
        children: "enum class"
      }), ".)"]
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.code, {
        children: "GetOwner()"
      }), " returns a pointer. It’s perfectly valid to add and subtract numbers to and from pointers; in fact this is a regular idiom in C code and low level C++ code. Pointer arithmetic lets you iterate through blocks of memory (for implementing arrays), and store data at specific memory locations."]
    }), "\n", _jsxs(_components.p, {
      children: ["Note that adding ", _jsx(_components.strong, {
        children: "1"
      }), " to a pointer won’t increase the memory address by ", _jsx(_components.strong, {
        children: "1"
      }), ", it will add it by ", _jsx(_components.code, {
        children: "1 * sizeof(T)"
      }), ", where ", _jsx(_components.code, {
        children: "T"
      }), " is the type you’re pointing to. We’ll see an example of this in a moment."]
    }), "\n", _jsx(HeaderWithAnchor, {
      id: "enum-implicit-conversion",
      number: 5,
      children: _jsx(_components.p, {
        children: "Enum implicit conversion 😱"
      })
    }), "\n", _jsxs(_components.p, {
      children: ["Here’s the fun bit: When you add an ", _jsx(_components.code, {
        children: "enum"
      }), " to a pointer, C++ will cast the ", _jsx(_components.code, {
        children: "enum"
      }), " to an ", _jsx(_components.code, {
        children: "int"
      }), " (or a larger integral type if required to hold the value, such as ", _jsx(_components.code, {
        children: "long long"
      }), "), and then add that to the pointer."]
    }), "\n", _jsx(_components.p, {
      children: "Let’s pull this together:"
    }), "\n", _jsx(EmbedGithubGist, {
      id: "e06fa79c552f36df57a3e79aa51d9960"
    }), "\n", _jsx(HeaderWithAnchor, {
      id: "what-about-the-comparison",
      number: 6,
      children: _jsx(_components.p, {
        children: "What about the comparison though?"
      })
    }), "\n", _jsxs(_components.p, {
      children: ["In the same vein as above — first the compiler converts the result of ", _jsx(_components.code, {
        children: "GetNetMode()"
      }), " to an ", _jsx(_components.code, {
        children: "int"
      }), " and subtracts it from the result of ", _jsx(_components.code, {
        children: "GetOwner()"
      }), ", creating a new pointer. This pointer is then compared to ", _jsx(_components.code, {
        children: "NM_DedicatedServer"
      }), ", both sides are converted to a matching integral type, and then they’re compared."]
    }), "\n", _jsx(DotDotDot, {}), "\n", _jsx(HeaderWithAnchor, {
      id: "how-to-avoid",
      number: 7,
      children: _jsx(_components.p, {
        children: "How can we avoid this happening? ✔️"
      })
    }), "\n", _jsx(_components.p, {
      children: "This is clearly confusing behaviour, and it’s easy to make this fail at compile time."
    }), "\n", _jsxs(_components.p, {
      children: ["Instead of using ", _jsx(_components.code, {
        children: "enum"
      }), ", we should declare ", _jsx(_components.code, {
        children: "ENetMode"
      }), " using ", _jsx(_components.code, {
        children: "enum class"
      }), " or as so:"]
    }), "\n", _jsx(EmbedGithubGist, {
      id: "90a9d9e71f9291e8ba9866ed166c7cc7"
    }), "\n", _jsxs(_components.p, {
      children: ["There are a ", _jsx(_components.a, {
        href: "https://stackoverflow.com/questions/12581064/enum-vs-strongly-typed-enum",
        children: "multitude of benefits"
      }), " to using ", _jsx(_components.code, {
        children: "enum class"
      }), ", but the one we care about is that implicit type conversion is no longer allowed. (Using ", _jsx(_components.code, {
        children: "static_cast"
      }), " to explicitly convert is all good though.)"]
    }), "\n", _jsx(EmbedLink, {
      url: "https://en.cppreference.com/w/cpp/language/enum#Scoped_enumerations"
    }), "\n", _jsx(HeaderWithAnchor, {
      id: "conclusion",
      number: 8,
      children: _jsx(_components.p, {
        children: "In conclusion"
      })
    }), "\n", _jsx(_components.p, {
      children: "Implicit type conversion can cause all types of unexpected headaches, so opting for writing code that requires us to be more explicit in our intent can save a lot of effort down the line."
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.code, {
        children: "enum class"
      }), " is one example of how we can write code that helps people using our code use it more correctly."]
    })]
  });
}
export default function MDXContent(props = {}) {
  return _jsx(MDXLayout, {
    ...props,
    children: _jsx(_createMdxContent, {
      ...props
    })
  });
}
